import React, { ReactElement, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, DataListItem, Input, Input2FA } from '@node-space/ui-components'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { BaseErrorResponse } from 'types/beneficiaries'
import { WalletType } from 'types/types'
import { roundNumberWithCommas } from 'utils/utils'
import { RefundFormPayload } from '../schemas/refundSchema'

interface RefundConfirmationProps {
  form: UseFormReturn<RefundFormPayload>
  formValues: RefundFormPayload
  wallet: WalletType
  isCreateRefundAuthRequestRequestLoading: boolean
  submitRefundError: BaseErrorResponse
  isOtpRequestError: boolean
  handleClose: () => void
  onPasswordSubmit: () => void
}

export const RefundConfirmation = ({
  form,
  formValues,
  wallet,
  submitRefundError,
  isCreateRefundAuthRequestRequestLoading,
  isOtpRequestError,
  handleClose,
  onPasswordSubmit,
}: RefundConfirmationProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()

  const dataItems = [
    {
      id: 'paymentAmount',
      title: t('amount'),
      body: `${roundNumberWithCommas(Number(formValues?.amount ?? 0))} ${wallet?.currency?.code}`,
    },
    {
      id: 'fee',
      title: t('fee'),
      body: `${roundNumberWithCommas(Number(formValues?.fee ?? 0))} ${wallet?.currency?.code}`,
    },
    {
      id: 'reference',
      title: t('paymentReference'),
      body: formValues?.reference,
    },
    {
      id: 'wallet',
      title: t('fromWallet'),
      body: wallet?.description,
    },
  ]

  const actionButtons = useMemo<[ReactElement, ReactElement]>(() => {
    if (!twoFactorEnabled) {
      return [
        <Button
          key="cancel"
          testId="refund-cancel"
          variant="secondary"
          onClick={() => handleClose()}
        >
          {t('cancel')}
        </Button>,
        <Button key="submit" testId="refund-submit" onClick={onPasswordSubmit}>
          {t('wallets.confirmRefund')}
        </Button>,
      ]
    }
  }, [twoFactorEnabled])

  const submitRefundErrorMessage = submitRefundError?.data as string

  const isPasswordError =
    !!isOtpRequestError && (submitRefundErrorMessage || t('authPasswordInvalid'))

  return (
    <Box>
      <Box paddingY={16}>
        {dataItems?.map(item => (
          <DataListItem key={item?.id} id={item?.id} title={item?.title} body={item?.body} />
        ))}
      </Box>

      {twoFactorEnabled ? (
        <Input2FA
          {...form?.register('password')}
          onChange={value => form?.setValue('password', value)}
          label={t('formFields.otp')}
          disabled={!formValues?.reference?.length}
          error={isPasswordError}
          loading={isCreateRefundAuthRequestRequestLoading}
        />
      ) : (
        <>
          <Input
            {...form?.register('password')}
            placeholder={t('password')}
            type="password"
            testId="password"
            onChange={e => {
              form?.setValue('password', e.target.value)
            }}
            value={formValues?.password}
            loading={isCreateRefundAuthRequestRequestLoading}
            error={!!isOtpRequestError}
            errorText={isPasswordError}
          />
          {actionButtons && (
            <Box flex justify="between" paddingT={20}>
              {actionButtons}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
